import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { useToast } from '@givebutter/primitives/toast';
import { faPlus, faUser, faUserXmark, faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    fetchCompanyContacts,
    makePointOfContact,
    removePointOfContact,
} from 'api/contacts/companies';
import * as AssociateContactDialog from 'assets/js/pages/contacts/companies/AssociateContactDialog';
import * as DisassociateContactDialog from 'assets/js/pages/contacts/companies/DisassociateContactDialog';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { accountQueryKeys } from 'queries/account-query-keys';
import { CompanyContact } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<CompanyContact>();

const CompanyContacts = () => {
    const { error, success } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: company } = useCurrentContact();
    const [search, setSearch] = usePersistentSearch();
    const debouncedSearch = useDebounce(search);
    const queryClient = useQueryClient();

    const { pagination, setPagination } = Table.useReactTableState();

    const [isPending, params] = useIsPending(
        {
            query: debouncedSearch,
            page: pagination.pageIndex + 1,
            sortBy: 'last_name',
        },
        500,
    );

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.companyContactsWithFilters(accountId, company.id, params),
        queryFn: () => fetchCompanyContacts(accountId, company.id, params),
    });

    const { mutate: addPointOfContact } = useMutation({
        mutationFn: (contactId: number) => {
            return makePointOfContact(accountId, company.id, {
                contact_id: contactId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contacts(accountId),
                type: 'all',
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.companyContacts(accountId, company.id),
                type: 'all',
            });
            success('Point of contact updated successfully.');
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Failed to update point of contact.');
        },
    });
    const { mutate: deletePointOfContact } = useMutation({
        mutationFn: (contactId: number) => {
            return removePointOfContact(accountId, company.id, {
                contact_id: contactId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contacts(accountId),
                type: 'all',
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.companyContacts(accountId, company.id),
                type: 'all',
            });
            success('Point of contact was removed successfully.');
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Failed to update point of contact.');
        },
    });

    const table = useReactTable({
        data: data.data.filter(item => item.contact),
        columns: [
            columnHelper.accessor('contact.first_name', {
                header: 'First name',
                cell: () => <Table.CellDataOrEmpty className={'font-medium'} />,
            }),
            columnHelper.accessor('contact.last_name', {
                header: 'Last name',
                cell: () => <Table.CellDataOrEmpty className={'font-medium'} />,
            }),
            columnHelper.accessor('contact.primary_email', {
                header: 'Email',
                cell: () => <Table.CellDataOrEmpty />,
            }),
            columnHelper.accessor('point_of_contact', {
                header: '',
                cell: info =>
                    info.row.original.contact.id === company.point_of_contact?.id && (
                        <div className="rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700">
                            Point of contact
                        </div>
                    ),
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const contact = info.row.original.contact;
                    return (
                        <DropdownMenu.Root key={contact.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align="end">
                                {info.row.original.contact.id === company.point_of_contact?.id ? (
                                    <DropdownMenu.Item
                                        onSelect={e => e.preventDefault()}
                                        onClick={() => deletePointOfContact(contact.id)}
                                        className="flex gap-2"
                                    >
                                        <FontAwesomeIcon icon={faUserXmark} />
                                        Clear primary contact
                                    </DropdownMenu.Item>
                                ) : (
                                    <DropdownMenu.Item
                                        onSelect={e => e.preventDefault()}
                                        onClick={() => addPointOfContact(contact.id)}
                                        className="flex gap-2"
                                    >
                                        <FontAwesomeIcon icon={faUser} />
                                        Make primary contact
                                    </DropdownMenu.Item>
                                )}
                                <DisassociateContactDialog.Root contactId={contact.id}>
                                    <DisassociateContactDialog.Trigger asChild>
                                        <DropdownMenu.Item
                                            onSelect={e => e.preventDefault()}
                                            className="flex gap-2"
                                        >
                                            <FontAwesomeIcon icon={faX} />
                                            Remove from company
                                        </DropdownMenu.Item>
                                    </DisassociateContactDialog.Trigger>
                                </DisassociateContactDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
                meta: {
                    ignoreLink: true,
                },
            }),
        ],
        pageCount: data?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            enableColumnOrder: true,
            to: cell => `/accounts/${accountId}/contacts/${cell.row.original.contact.id}`,
        },
    });

    return (
        <Table.Root isLoading={isPending} table={table} variant="bordered">
            <Box className="flex flex-col overflow-hidden">
                <div className={'flex flex-col'}>
                    <div className={'flex justify-between gap-4 border-b border-b-gray-100 p-4'}>
                        <div className={'flex flex-wrap gap-1.5'}>
                            <Label className="sr-only" htmlFor="company_contacts_search">
                                Search
                            </Label>
                            <Input.Root size={'sm'}>
                                <Input.SearchIcon />
                                <Input.Form
                                    id="company_contacts_search"
                                    placeholder={'Search'}
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </Input.Root>
                        </div>
                        <div className={'flex gap-1'}>
                            <Table.ResultsCount />
                        </div>
                    </div>
                </div>
                <Table.Content>
                    <Table.Header />
                    <Table.Body />
                </Table.Content>
                <Table.Empty variant={'primary'} className="max-w-full gap-1">
                    <div className="flex flex-col items-center justify-center gap-3">
                        <Table.EmptyIcon>
                            <FontAwesomeIcon icon={faUser} />
                        </Table.EmptyIcon>
                        <Table.EmptyTitle>No contacts</Table.EmptyTitle>
                        <Table.EmptyDescription>
                            Add any employees or associated contacts to this company.
                        </Table.EmptyDescription>
                        <AssociateContactDialog.Root>
                            <AssociateContactDialog.Trigger asChild>
                                <Button variant="solid">
                                    <FontAwesomeIcon icon={faPlus} /> Add contact
                                </Button>
                            </AssociateContactDialog.Trigger>
                        </AssociateContactDialog.Root>
                    </div>
                </Table.Empty>
                <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                    <Table.PaginationPageNavigation />
                </Table.Pagination>
            </Box>
        </Table.Root>
    );
};

export { CompanyContacts };
